import { Element, useNode } from "@craftjs/core";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Box, Button, FormControl, FormControlLabel, FormLabel, Stack, Switch, TextField } from "@mui/material";
import { CContainer } from "./CContainer";
import { CText } from "./CText";
import { Pagination, Navigation } from 'swiper/modules';

interface CCarouselProps {
  slides?: string[];
  paddingV?: any;
  paddingH?: any;
  showPagination?: boolean;
  showNavigation?: boolean;
  slideItemWidth?: any;
  slidesPerView?: any;
  slideSpaceBetween?: any;
  [key: string]: any;
}

export const CCarousel = ({ 
  slides = [],
  paddingV, 
  paddingH,
  showPagination,
  showNavigation,
  slideItemWidth,
  slidesPerView,
  slideSpaceBetween,
  ...props
}: CCarouselProps) => {
  const {
    connectors: { connect, drag },
    selected
  } = useNode((node) => ({
    selected: node.events.selected
  }));

  const pmstr = (value: any) => {
    return (!isNaN(value) && !isNaN(parseFloat(value))) ? `${value}px` : value;
  }

  return (
    <Stack 
      ref={(ref: any) => connect(drag(ref))} 
      width="100%"
      paddingLeft={pmstr(paddingH)}
      paddingRight={pmstr(paddingH)}
      paddingTop={pmstr(paddingV)}
      paddingBottom={pmstr(paddingV)}
      className="craft-carousel"
      style={{
        borderColor: selected ? '#008522' : '#F0F0F0',
      }}
      {...props}
    >
      <Box>
        <Swiper 
          spaceBetween={slideSpaceBetween} 
          slidesPerView={ slidesPerView }
          navigation={ showNavigation }
          pagination={ showPagination }
          modules={[Pagination, Navigation]}
        >
          {slides.map((slider_name) => (
            <SwiperSlide key={slider_name} style={{
              width: slideItemWidth,
            }}>
              <Box style={{ width: "100%", minHeight: "100px" }}>
                <Element
                  canvas
                  is={CContainer}
                  paddingH={10}
                  paddingV={0}
                  flexDirection="Column"
                  verticalAlign="center"
                  horizontalAlign="center"
                  data-cy="frame-container"
                  width="100%"
                  height="100px"
                  id={`${slider_name}_content_wrapper`}
                >
                  <CText textAlign="center" variant="p" fontSize={16} color="#323232" text={slider_name} data-cy="frame-text" />
                </Element>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Stack>
  );
};

export const CCarouselSettings = () => {
  const {
    slides,
    paddingH,
    paddingV,
    showNavigation,
    showPagination,
    slideItemWidth,
    slidesPerView,
    slideSpaceBetween,
    actions: { setProp },
  } = useNode((node) => ({
    slides: node.data.props.slides,
    paddingH: node.data.props.paddingH,
    paddingV: node.data.props.paddingV,
    showNavigation: node.data.props.showNavigation,
    showPagination: node.data.props.showPagination,
    slideItemWidth: node.data.props.slideItemWidth,
    slidesPerView: node.data.props.slidesPerView,
    slideSpaceBetween: node.data.props.slideSpaceBetween,
  }));

  const handleAddSlide = () => {
    let lastSliderIndex = 0;
    for (const slide of slides) {
      const index = parseInt(slide.replace('slide_', ''));
      lastSliderIndex = Math.max(lastSliderIndex, index);
    }
    setProp((props: any) => props.slides.push(`slide_${lastSliderIndex + 1}`));
  };

  const handleRemoveSlide = (index: number) => {
    setProp((props: any) => props.slides.splice(index, 1));
  };

  const handleInputChange = (index: number, value: string) => {
    setProp((props: any) => (props.slides[index] = value));
  };

  return (
    <Stack spacing={2} paddingBottom={4}>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Vertical</FormLabel>
          <TextField
            value={paddingV}
            onChange={(e) =>
              setProp((props: any) => (props.paddingV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Horizontal</FormLabel>
          <TextField
            value={paddingH}
            onChange={(e) =>
              setProp((props: any) => (props.paddingH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormControlLabel
            sx={{
              '&>span': {
                padding: '12px !important',
              }
            }}
            control={
              <Switch
                checked={showNavigation}
                onChange={(e) =>
                  setProp((props: any) => (props.showNavigation = e.target.checked))
                }
              />
            }
            label={'Show Navigation'}
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
        <FormControlLabel
            sx={{
              '&>span': {
                padding: '12px !important',
              }
            }}
            control={
              <Switch
                checked={showPagination}
                onChange={(e) =>
                  setProp((props: any) => (props.showPagination = e.target.checked))
                }
              />
            }
            label={'Show Pagination'}
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Vertical</FormLabel>
          <TextField
            value={paddingV}
            onChange={(e) =>
              setProp((props: any) => (props.paddingV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Horizontal</FormLabel>
          <TextField
            value={paddingH}
            onChange={(e) =>
              setProp((props: any) => (props.paddingH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Slide Item Width</FormLabel>
          <TextField
            value={slideItemWidth}
            onChange={(e) =>
              setProp((props: any) => (props.slideItemWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Slides Per View</FormLabel>
          <TextField
            value={slidesPerView}
            onChange={(e) =>
              setProp((props: any) => (props.slidesPerView = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Slide Space Between</FormLabel>
          <TextField
            value={slideSpaceBetween}
            onChange={(e) =>
              setProp((props: any) => (props.slideSpaceBetween = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        {/* <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Slides Per View</FormLabel>
          <TextField
            value={slidesPerView}
            onChange={(e) =>
              setProp((props: any) => (props.slidesPerView = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl> */}
      </Stack>
      <Stack paddingTop={4}>
        <Button variant="contained" color="primary" onClick={handleAddSlide}>
          Add Slide
        </Button>
      </Stack>
      {slides.map((slide: string, index: number) => (
        <Stack key={index} direction="row" spacing={1} marginBottom={2} p={1} borderBottom="1px solid #F0F0F0">
          <FormControl style={{ flexGrow: 1 }} size="small">
            <TextField
              value={slide}
              disabled
              onChange={(e) =>
                handleInputChange(index, e.target.value)
              }
              variant="outlined"
              size="small"
            />
          </FormControl>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleRemoveSlide(index)}
          >
            Remove
          </Button>
        </Stack>
      ))}
    </Stack>
  );
};

export const CCarouselProps: CCarouselProps = {
  paddingH: '10px',
  paddingV: '10px',
  showNavigation: false,
  showPagination: true,
  slideItemWidth: 'auto',
  slidesPerView: 'auto',
  slideSpaceBetween: '17px',
  slides: [
    "slide_1",
    "slide_2",
    "slide_3",
  ],
};

CCarousel.craft = {
  displayName: "Carousel",
  props: CCarouselProps,
  related: {
    settings: CCarouselSettings,
  },
};
